import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Target, Users, BarChart3, Mail, Zap, Sparkles, TrendingUp } from 'lucide-react';
import { Layout } from '../components/Layout';
import { useIsMobile } from '../lib/useIsMobile';
export function BrandsPage() {
  const isMobile = useIsMobile();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const services = [{
    title: 'Campaign Strategy',
    description: 'Data-driven campaign planning that aligns with your brand goals, ensuring maximum ROI and audience resonance.',
    icon: Target,
    color: 'from-orange-400 to-red-400',
    shadow: 'rgba(251,146,60,0.4)'
  }, {
    title: 'Creator Matching',
    description: 'Precise vetting and selection of creators who align with your brand values and speak authentically to your target demographic.',
    icon: Users,
    color: 'from-yellow-400 to-orange-400',
    shadow: 'rgba(255,211,61,0.4)'
  }, {
    title: 'Campaign Execution',
    description: 'Comprehensive management of the entire campaign lifecycle, from initial outreach to final performance reporting.',
    icon: BarChart3,
    color: 'from-pink-400 to-rose-400',
    shadow: 'rgba(244,114,182,0.4)'
  }];
  const bounceTransition = {
    type: 'spring',
    stiffness: 260,
    damping: 20
  };
  return <Layout data-id="element-182">
      {/* Hero Section - Added more top padding */}
      <div className="pt-40 md:pt-48 pb-12 md:pb-20 px-4 md:px-8 max-w-7xl mx-auto relative overflow-hidden" data-id="element-183">
        <motion.div initial={{
        y: 50,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} transition={bounceTransition} className="mb-12 md:mb-20 relative z-10" data-id="element-184">
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-fredoka font-bold text-gray-800 mb-6 leading-tight" data-id="element-185">
            Influencer{' '}
            <motion.span className="inline-block px-6 py-3 bg-gradient-to-r from-yellow-400 to-orange-400 text-white rounded-3xl border-4 border-gray-800 shadow-[6px_6px_0px_rgba(45,55,72,1)]" animate={{
            rotate: [2, -2, 2],
            y: [0, -8, 0]
          }} transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-186">
              Marketing
            </motion.span>
          </h1>
          <p className="text-lg md:text-2xl text-gray-700 max-w-2xl font-bold leading-relaxed" data-id="element-187">
            Connect with authentic storytellers. We craft influencer marketing
            campaigns that cut through the noise and drive real business
            results.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 relative z-10" data-id="element-188">
          {services.map((service, index) => <motion.div key={service.title} initial={{
          y: 50,
          opacity: 0,
          rotate: 3
        }} whileInView={{
          y: 0,
          opacity: 1,
          rotate: 0
        }} viewport={{
          once: true
        }} transition={{
          ...bounceTransition,
          delay: index * 0.1
        }} className="group relative" data-id="element-189">
              <div className="relative p-6 md:p-8 rounded-3xl bg-white border-4 border-gray-800 transition-all duration-300 hover:translate-x-[-4px] hover:translate-y-[-4px]" style={{
            boxShadow: `12px 12px 0px ${service.shadow}`
          }} onMouseEnter={e => {
            e.currentTarget.style.boxShadow = `16px 16px 0px ${service.shadow}`;
          }} onMouseLeave={e => {
            e.currentTarget.style.boxShadow = `12px 12px 0px ${service.shadow}`;
          }} data-id="element-190">
                <div className={`mb-6 inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br ${service.color} border-3 border-gray-800 text-white group-hover:scale-110 group-hover:rotate-12 transition-all duration-300`} data-id="element-191">
                  <service.icon className="w-7 h-7" data-id="element-192" />
                </div>
                <h3 className="text-2xl md:text-3xl font-fredoka font-bold text-gray-800 mb-4 group-hover:scale-105 transition-transform" data-id="element-193">
                  {service.title}
                </h3>
                <p className="text-gray-600 leading-relaxed font-medium text-base" data-id="element-194">
                  {service.description}
                </p>
              </div>
            </motion.div>)}
        </div>
      </div>

      {/* Stats Section */}
      <section className="py-16 md:py-24 px-4 md:px-8 relative overflow-hidden" data-id="element-195">
        <div className="max-w-5xl mx-auto relative" data-id="element-196">
          <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} whileInView={{
          scale: 1,
          opacity: 1
        }} viewport={{
          once: true
        }} transition={bounceTransition} className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-197">
            {/* Stat 1 */}
            <div className="relative p-8 md:p-12 rounded-3xl bg-white border-4 border-gray-800 shadow-[12px_12px_0px_rgba(251,146,60,0.4)] hover:shadow-[16px_16px_0px_rgba(251,146,60,0.5)] hover:translate-x-[-4px] hover:translate-y-[-4px] transition-all duration-300" data-id="element-198">
              <div className="mb-4 inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-400 to-red-400 border-3 border-gray-800 text-white" data-id="element-199">
                <TrendingUp className="w-6 h-6" data-id="element-200" />
              </div>
              <h3 className="text-5xl md:text-6xl font-fredoka font-bold text-gray-800 mb-3" data-id="element-201">
                Millions
              </h3>
              <p className="text-gray-600 font-bold text-sm tracking-wide uppercase" data-id="element-202">
                Views Generated for Brands
              </p>
            </div>

            {/* Stat 2 */}
            <div className="relative p-8 md:p-12 rounded-3xl bg-white border-4 border-gray-800 shadow-[12px_12px_0px_rgba(255,211,61,0.4)] hover:shadow-[16px_16px_0px_rgba(255,211,61,0.5)] hover:translate-x-[-4px] hover:translate-y-[-4px] transition-all duration-300" data-id="element-203">
              <div className="mb-4 inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br from-yellow-400 to-orange-400 border-3 border-gray-800 text-white" data-id="element-204">
                <Zap className="w-6 h-6" data-id="element-205" />
              </div>
              <h3 className="text-5xl md:text-6xl font-fredoka font-bold text-gray-800 mb-3" data-id="element-206">
                Six Figure
              </h3>
              <p className="text-gray-600 font-bold text-sm tracking-wide uppercase" data-id="element-207">
                Brand Spend
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section - Changed to green/emerald theme */}
      <section className="py-16 md:py-32 px-4 md:px-8 relative overflow-hidden bg-gradient-to-br from-green-50 to-emerald-50" data-id="element-208">
        {/* Decorative blob */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-green-200 blob opacity-30" data-id="element-209" />

        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-210">
          <motion.div initial={{
          y: 30,
          opacity: 0
        }} whileInView={{
          y: 0,
          opacity: 1
        }} viewport={{
          once: true
        }} transition={bounceTransition} data-id="element-211">
            <span className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-green-300 to-emerald-300 border-3 border-gray-800 text-gray-800 font-bold text-sm tracking-wider uppercase shadow-[4px_4px_0px_rgba(45,55,72,0.3)] mb-8" data-id="element-212">
              <Zap className="w-5 h-5" data-id="element-213" />
              Ready to Scale?
            </span>

            <h2 className="text-4xl md:text-6xl lg:text-7xl font-fredoka font-bold text-gray-800 mb-6 leading-tight" data-id="element-214">
              Amplify Your{' '}
              <span className="text-gradient-playful" data-id="element-215">Brand's Reach</span>{' '}
              <span className="inline-block text-5xl md:text-7xl animate-bounce-gentle" data-id="element-216">
                🎯
              </span>
            </h2>

            <p className="text-lg md:text-xl text-gray-700 max-w-2xl mx-auto mb-12 font-bold leading-relaxed" data-id="element-217">
              Partner with creators who authentically connect with your target
              audience. Transform your marketing with campaigns that deliver
              real results.
            </p>
          </motion.div>

          <motion.div initial={{
          y: 30,
          opacity: 0
        }} whileInView={{
          y: 0,
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          ...bounceTransition,
          delay: 0.2
        }} data-id="element-218">
            <a href="mailto:hello@horizonima.com" className="btn-playful inline-flex items-center gap-3 group" data-id="element-219">
              <Mail className="w-5 h-5 group-hover:rotate-12 transition-transform" data-id="element-220" />
              <span className="text-lg" data-id="element-221">Get In Touch</span>
              <Sparkles className="w-5 h-5 group-hover:scale-125 transition-transform" data-id="element-222" />
            </a>
          </motion.div>
        </div>
      </section>
    </Layout>;
}